/**
 * Sprint 006 (A2) — Shared status badge.
 *
 * Elevates the expense-pill anatomy (soft fill + border ring + light
 * text) to the app-wide standard. Domain code maps its status values to
 * a semantic tone; the badge owns the visual language.
 *
 * Ratified role map (Sprint-006 Entscheidung, Plan § A2):
 *   neutral — draft (überall)
 *   info    — sent / quartal-closed / completed
 *   success — paid / active / accepted / geprüft
 *   warning — paused / deferred / expired / rückfrage / ocr-fail / missing-fields
 *   danger  — overdue / betragsabweichung / rejected
 *   muted   — terminal states: cancelled / archiviert / nicht_relevant / zurückgezogen
 *   special — converted
 */
import { type ReactNode } from 'react';

export type StatusTone =
  | 'neutral'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'
  | 'muted'
  | 'special';

export const STATUS_TONE_CLASSES: Record<StatusTone, string> = {
  neutral: 'bg-white/10 border-white/20 text-white/80',
  info: 'bg-blue-500/10 border-blue-500/30 text-blue-200',
  success: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200',
  warning: 'bg-amber-500/10 border-amber-500/30 text-amber-200',
  danger: 'bg-red-500/15 border-red-500/40 text-red-200',
  muted: 'bg-white/5 border-white/10 text-white/50',
  special: 'bg-purple-500/10 border-purple-500/30 text-purple-200',
};

const SIZE_CLASSES = {
  /** Table rows, dense lists */
  sm: 'px-2 py-0.5 text-[11px]',
  /** Detail pages, cards */
  md: 'px-2.5 py-1 text-xs',
} as const;

interface StatusBadgeProps {
  tone: StatusTone;
  children: ReactNode;
  size?: keyof typeof SIZE_CLASSES;
  title?: string;
  className?: string;
  'data-testid'?: string;
}

export function StatusBadge({
  tone,
  children,
  size = 'sm',
  title,
  className = '',
  ...rest
}: StatusBadgeProps) {
  return (
    <span
      title={title}
      className={`inline-flex items-center whitespace-nowrap rounded-full border font-medium ${STATUS_TONE_CLASSES[tone]} ${SIZE_CLASSES[size]} ${className}`}
      {...rest}
    >
      {children}
    </span>
  );
}

export default StatusBadge;
